// client/src/components/auth/RegisterWithInvite.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../layout/LanguageSelector';
import { sanitizeInput } from '../utils/sanitize';

const RegisterWithInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language.startsWith('it') ? 'it' : 'en';
  
  // ✅ STATES
  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false); // ✅ NEW STATE
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    position: '',
    acceptedTerms: false
  });

  const validateInvite = useCallback(async () => {
    try {
      setValidating(true);
      
      const response = await axios.get(`/api/team/invite/validate/${token}`);
      
      if (response.data.success && response.data.invite) {
        setInviteData(response.data.invite);
        setFormData(prev => ({
          ...prev,
          email: response.data.invite.email
        }));
        setError('');
      } else {
        setError('Invalid invitation data received');
      }
    } catch (err) {
      console.error('❌ Invite validation error:', err);
      const errorMessage = err.response?.data?.message || t('auth.invite.invalidLink');
      setError(errorMessage);
    } finally {
      setValidating(false);
    }
  }, [token, t]);

  useEffect(() => {
    validateInvite();
  }, [validateInvite]);

  const onChange = e => {
    if (e.target.type === 'checkbox') {
      setFormData({ ...formData, [e.target.name]: e.target.checked });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
    
    if (error) {
      setError('');
    }
  };

  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);

  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    
    // ✅ VALIDATION
    if (!formData.firstName.trim()) {
      return setError(t('auth.register.errors.firstNameRequired') || 'First name is required');
    }
    
    if (!formData.lastName.trim()) {
      return setError(t('auth.register.errors.lastNameRequired') || 'Last name is required');
    }
    
    if (!formData.position.trim()) {
      return setError(t('auth.register.errors.positionRequired') || 'Position is required');
    }
    
    if (formData.password !== formData.confirmPassword) {
      return setError(t('auth.register.errors.passwordMismatch') || 'Passwords do not match');
    }
    
    if (formData.password.length < 10) {
      return setError(t('auth.register.errors.passwordTooShort') || 'Password must be at least 10 characters');
    }
    
    if (!/[A-Z]/.test(formData.password) || 
        !/[a-z]/.test(formData.password) || 
        !/[0-9]/.test(formData.password) || 
        !/[^A-Za-z0-9]/.test(formData.password)) {
      return setError(t('auth.register.errors.passwordWeak') || 'Password must contain uppercase, lowercase, number and special character');
    }
    
    if (!formData.acceptedTerms) {
      return setError(t('auth.register.errors.acceptTermsRequired') || 'You must accept the terms and conditions');
    }
    
    setSubmitting(true);
    
    try {
      const sanitizedData = {
        firstName: sanitizeInput(formData.firstName.trim()),
        lastName: sanitizeInput(formData.lastName.trim()),
        email: formData.email,
        password: formData.password,
        phone: sanitizeInput(formData.phone.trim()),
        position: sanitizeInput(formData.position.trim()),
        acceptedTerms: formData.acceptedTerms,
        preferredLanguage: selectedLanguage
      };
      
      const response = await axios.post(`/api/auth/register/invite/${token}`, sanitizedData);
      
      // ✅ SHOW SUCCESS PAGE INSTEAD OF AUTO-LOGIN
      setRegistrationComplete(true);
      
    } catch (err) {
      const errorMessage = err.response?.data?.message || t('auth.register.errors.registrationFailed') || 'Registration failed';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  // ✅ LOADING STATE
  if (validating) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{t('auth.invite.validating') || 'Validating invitation...'}</p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ ERROR STATE
  if (error && !inviteData) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>{t('auth.invite.errorTitle') || 'Invitation Error'}</h2>
          <div className="alert alert-danger">{error}</div>
          <Link to="/login" className="btn btn-primary btn-block">
            {t('auth.invite.goToLogin') || 'Go to Login'}
          </Link>
        </div>
      </div>
    );
  }

  // ✅ SUCCESS STATE - NEW PAGE
  if (registrationComplete) {
    return (
      <div className="auth-container">
        {/* Language Selector */}
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        
        <div className="auth-card">
          <div className="success-content">
            <div className="success-icon">
              <div className="checkmark">✓</div>
            </div>
            
            <h2>{t('auth.invite.successTitle') || 'Registration Completed Successfully!'}</h2>
            
            <p className="success-message">
              {t('auth.invite.successMessage') || 'You can now log in for the first time!'}
            </p>
            
            {inviteData && (
              <div className="success-organization">
                <p>Welcome to <strong>{inviteData.organization.name}</strong></p>
              </div>
            )}
            
            <button 
              onClick={handleGoToLogin}
              className="btn btn-primary btn-block btn-large"
            >
              {t('auth.invite.goToLoginButton') || 'Go to Login'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ REGISTRATION FORM
  return (
    <div className="auth-container">
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      <div className="auth-card">
        <h2>{t('auth.invite.title') || 'Complete Your Registration'}</h2>
        
        {inviteData && (
          <div className="invite-info">
            <p>{t('auth.invite.invitedBy', { 
              name: `${inviteData.invitedBy.firstName} ${inviteData.invitedBy.lastName}` 
            }) || `Invited by ${inviteData.invitedBy.firstName} ${inviteData.invitedBy.lastName}`}</p>
            <p className="organization-name">{inviteData.organization.name}</p>
          </div>
        )}
        
        {error && <div className="alert alert-danger">{error}</div>}
        
        <form onSubmit={onSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('auth.register.firstName') || 'First Name'}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={onChange}
                required
                disabled={submitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">{t('auth.register.lastName') || 'Last Name'}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={onChange}
                required
                disabled={submitting}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="email">{t('auth.register.emailAddress') || 'Email Address'}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              readOnly
              className="form-control-readonly"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="phone">{t('auth.register.phoneNumber') || 'Phone Number'}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={onChange}
                disabled={submitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="position">{t('auth.register.position') || 'Position'}</label>
              <input
                type="text"
                id="position"
                name="position"
                value={formData.position}
                onChange={onChange}
                required
                disabled={submitting}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">{t('auth.register.password') || 'Password'}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={onChange}
                required
                minLength="10"
                disabled={submitting}
              />
              <small className="form-text text-muted">
                {t('auth.register.passwordRequirements') || 'Password must be at least 10 characters with uppercase, lowercase, number and special character'}
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">{t('auth.register.confirmPassword') || 'Confirm Password'}</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={onChange}
                required
                disabled={submitting}
              />
            </div>
          </div>

          <div className="form-group">
              <label htmlFor="preferredLanguage">{t('auth.register.preferredLanguage') || 'Preferred Language'}</label>
              <select
                id="preferredLanguage"
                name="preferredLanguage"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                disabled={submitting}
                required
                className="form-control"
              >
                <option value="it">Italiano</option>
                <option value="en">English</option>
              </select>
            </div>
          
          <div className="form-group checkbox-group">
            <div className="checkbox-label">
              <input
                type="checkbox"
                id="acceptedTerms"
                name="acceptedTerms"
                checked={formData.acceptedTerms}
                onChange={onChange}
                required
              />
              <span>
                {t('auth.register.acceptTerms')}{' '}
                <a 
                  href={currentLanguage === 'en' ? "/Termini e Condizioni di Utilizzo_eng.pdf" : "/Termini e Condizioni di Utilizzo_ita.pdf"}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="primary-link"
                >
                  {t('auth.register.termsConditions')}
                </a>
                {' e '}
                <a 
                  href={currentLanguage === 'en' ? "/Informativa Privacy_eng.pdf" : "/Informativa Privacy_ita.pdf"}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="primary-link"
                >
                  {t('auth.register.privacyPolicy')}
                </a>
              </span>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={submitting || !inviteData}
          >
            {submitting ? 
              (t('auth.invite.registering') || 'Registering...') : 
              (t('auth.invite.completeRegistration') || 'Complete Registration')
            }
          </button>
        </form>
        
        <div className="auth-links">
          <p>
            {t('auth.register.alreadyAccount') || 'Already have an account?'}{' '}
            <Link to="/login">{t('auth.register.loginHere') || 'Login here'}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterWithInvite;